import { useState, useRef, useEffect, useContext, useCallback } from 'react';

import {
  Avatar,
  Button as BokehButton,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
  HStack,
  Box,
  Badge,
  Tooltip,
  useToast,
} from '@vimeo/bokeh';
import { CircleShape, CircleCheckFilled, EllipsisV, PaginationDotFilled } from '@vimeo/bokeh-icons';
import { useMarkAsReadOnVisibility } from '@vimeo/comments-tracking';
import { StaffPickBadge } from '@vimeo/components';
import { useCapability } from '@vimeo/core/hooks/use-capability';
import { fromNow } from '@vimeo/core/libs/date';

import { T } from '../../translations';
import { CommentProps } from '../../types';
import { convertDateTime, getAvatarImages } from '../../utils';
import { CommentHighlightContext } from '../CommentHighlightContext';
import { CommentClipPreviews } from './CommentClipPreviews';
import { RichCommentRender } from './RichCommentRender';
import { useReportCommentTestAssignment } from './use-report-comment-test-assignment';

/**
 * A single comment
 * If a top level comment then display reply button
 * If a top level comment and has replies then display replies toggle
 * On mobile menu button is always visible - on desktop it's visible on hover
 */
export const Comment = ({
  id,
  user,
  createdTime,
  editedTime,
  timeCode,
  formattedTimeCode,
  text,
  replies,
  isReply,
  onEditClick,
  onDeleteClick,
  onCopyClick,
  onReplyClick,
  isOwnComment,
  isOwnVideo,
  activeReplyId,
  isDeeplinkedComment = false,
  isRichComment = false,
  canAddComments = true,
  onOpenCommentOptionsClick,
  isUserProfileClickable,
  onUserProfileClick,
  status,
  toggleCommentStatus,
  canResolveComment = false,
  textDecorations,
  setPreviewClip,
  onCommentsClick,
  isBeingEdited,
  parentCommentId,
  searchQuery,
  isPreviousVersion = false,
  onReportClick,
  videoPrivacy,
  isPublic,
  isUnread,
}: CommentProps) => {
  // Track if menu is open so we can keep the button visible when comment is not being hovered
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { capabilities } = useCapability(['hasCommentsTrackingEnabled']);
  const isCommentsTrackingEnabled = capabilities?.hasCommentsTrackingEnabled;
  const isClosed = status === 'closed';
  const [avaSrc, avaSrcSet] = getAvatarImages(user?.pictures?.sizes);
  const isBeingRepliedTo = activeReplyId && activeReplyId === id;
  const hasReplies = replies && replies.length > 0;
  const showReplyButton =
    !isReply && !isBeingRepliedTo && !isBeingEdited && !hasReplies && canAddComments;
  const shouldShowDelete = isOwnComment || isOwnVideo;
  const shouldShowEdit = isOwnComment && (canAddComments || isPreviousVersion) && !isClosed;
  const reportCommentTestAssignment = useReportCommentTestAssignment();
  const shouldShowReport =
    isPublic &&
    videoPrivacy === 'anybody' &&
    !isOwnComment &&
    onReportClick !== undefined &&
    reportCommentTestAssignment === 'variant';
  const shouldShowCopyLink = onCopyClick && !isPreviousVersion;
  const deeplinkedCommentRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const TOAST_DURATION = 2000;

  const onMenuOpen = () => {
    onOpenCommentOptionsClick?.(id, !!isReply, user?.id || '', Boolean(timeCode));
    setIsMenuOpen(true);
  };

  const onUserProfileClickHandler = () => {
    onUserProfileClick?.(id, !!isReply, user?.id || '');
  };

  const { highlightedCommentId, setHighlightedCommentId } = useContext(CommentHighlightContext);
  const isHighlightedOnTimeline = highlightedCommentId === id;

  useEffect(() => {
    setTimeout(
      () =>
        (isDeeplinkedComment || isHighlightedOnTimeline) &&
        deeplinkedCommentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }),
      500,
    );
  }, [isDeeplinkedComment, isHighlightedOnTimeline]);

  const userBadge = user?.badge;
  const showResolve = status && !isReply && canResolveComment && !isPreviousVersion;

  const commentHeaderTextRef = useRef<HTMLDivElement>(null);
  const [showCommentHeaderTooltip, setShowCommentHeaderTooltip] = useState(false);
  useEffect(() => {
    const element = commentHeaderTextRef.current;
    if (!element) return;
    const resizeObserver = new ResizeObserver(() => {
      setShowCommentHeaderTooltip(element.scrollWidth > element.clientWidth);
    });
    resizeObserver.observe(element);
    return () => {
      if (!element) return;
      resizeObserver.disconnect();
    };
  }, [commentHeaderTextRef]);

  // Track comment visibility and mark as read when 80% visible for 1 second
  const commentContainerRef = useMarkAsReadOnVisibility({
    commentId: id,
    isUnread: isUnread ?? false,
  });

  // Merge refs for deeplinked comments and visibility tracking
  const mergedRef = useCallback(
    (node: HTMLDivElement | null) => {
      // Assign to deeplinked ref if needed
      if (node && (isDeeplinkedComment || isHighlightedOnTimeline)) {
        (deeplinkedCommentRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
      // Assign to visibility tracking ref only if tracking is enabled and comment is unread
      if (isCommentsTrackingEnabled && isUnread) {
        (commentContainerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
    [
      isDeeplinkedComment,
      isHighlightedOnTimeline,
      isCommentsTrackingEnabled,
      isUnread,
      commentContainerRef,
    ],
  );

  return (
    <HStack
      spacing={75}
      align="flex-start"
      mx="8px"
      px={{ base: '8px', md: '16px' }}
      py={75}
      role="group"
      data-testid="comment"
      ref={mergedRef}
      onClick={() => {
        if (timeCode && onCommentsClick) {
          onCommentsClick();
        }
      }}
      id={`comment_${id.split('/').pop()}`}
      onMouseEnter={() => {
        if (timeCode) {
          setHighlightedCommentId(id);
        }
      }}
      onMouseLeave={() => {
        if (timeCode) {
          setHighlightedCommentId(null);
        }
      }}
      backgroundColor={isHighlightedOnTimeline ? 'fill-component-hover' : 'inherit'}
      borderRadius="md"
      sx={{
        'p, span, img, strong': {
          opacity: status === 'closed' ? 0.5 : undefined,
        },
        'strong, strong span, strong span p': {
          opacity: status === 'closed' ? 1 : undefined,
        },
      }}
      _hover={{
        cursor: timeCode ? 'pointer' : 'default',
        background: 'fill-component-hover',
      }}
    >
      {user?.link && isUserProfileClickable ? (
        <a onClick={onUserProfileClickHandler} href={user?.link} aria-label="Link to user profile">
          <Avatar
            data-testid="avatar"
            alt="User avatar"
            src={avaSrc}
            srcSet={avaSrcSet}
            size="sm"
            style={{ height: '2rem' }} // Fixed height prevent jump when comment group height is animated
          />
        </a>
      ) : (
        <Avatar
          data-testid="avatar"
          alt="User avatar"
          src={avaSrc}
          srcSet={avaSrcSet}
          size="sm"
          style={{ height: '2rem' }} // Fixed height prevent jump when comment group height is animated
        />
      )}
      <VStack spacing={75} minW={0} align="stretch" flex={1}>
        <HStack spacing={75} mb="-8px">
          {user?.link && isUserProfileClickable ? (
            <a
              onClick={onUserProfileClickHandler}
              href={user?.link}
              aria-label="Link to user profile"
              style={{ minWidth: 0 }}
            >
              <Tooltip placement="top" isDisabled={!showCommentHeaderTooltip} label={user?.name}>
                <Text
                  variant="heading-xs"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  ref={commentHeaderTextRef}
                  color="text-primary"
                >
                  {user?.name}
                </Text>
              </Tooltip>
            </a>
          ) : (
            <Tooltip placement="top" isDisabled={!showCommentHeaderTooltip} label={user?.name}>
              <Text
                ref={commentHeaderTextRef}
                variant="heading-xs"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                color="text-primary"
              >
                {user?.name}
              </Text>
            </Tooltip>
          )}
          {userBadge === 'alum' && <Badge size="sm">{T.Alum}</Badge>}
          {userBadge === 'staff' && (
            <Badge variant={userBadge} size="sm">
              {T.Staff}
            </Badge>
          )}
          {user && user.uri && user.isStaffPicked && (
            <StaffPickBadge
              href="/watch"
              videoId={parseInt(id, 10)}
              userUri={user.uri}
              feature="comments"
              marginRight={0}
            />
          )}
          <Box>
            <Tooltip placement="top" label={convertDateTime(createdTime)}>
              <Text
                variant="body-sm"
                as="span"
                whiteSpace="nowrap"
                flexGrow={1}
                color="text-primary"
              >
                {fromNow(new Date(createdTime), { shorten: true })}
              </Text>
            </Tooltip>
          </Box>
          {(shouldShowEdit || shouldShowCopyLink || shouldShowDelete) && (
            <Menu onOpen={onMenuOpen} onClose={() => setIsMenuOpen(false)}>
              <MenuButton
                as={IconButton}
                aria-label="Options"
                icon={<EllipsisV />}
                variant="minimal"
                size="xs"
                ml="auto"
                opacity={{ base: 1, md: isMenuOpen ? 1 : 0 }}
                _groupHover={{ opacity: 1 }}
                _focus={{ opacity: 1 }}
                data-testid="comment-menu-button"
              />
              <MenuList background="background" minW="169px">
                {shouldShowEdit ? (
                  <MenuItem onClick={() => onEditClick(id)}>{T.Edit}</MenuItem>
                ) : null}
                {shouldShowCopyLink ? (
                  <MenuItem
                    onClick={() => {
                      onCopyClick(id, user?.id || '', Boolean(timeCode), parentCommentId, text);
                      toast({
                        duration: TOAST_DURATION,
                        title: T.LinkCopied,
                      });
                    }}
                  >
                    {T.CopyLink}
                  </MenuItem>
                ) : null}
                {shouldShowReport ? (
                  <MenuItem
                    onClick={() =>
                      onReportClick(id.split('/').pop(), isReply, user?.id?.split('/').pop())
                    }
                  >
                    {T.Report}
                  </MenuItem>
                ) : null}
                {shouldShowDelete ? (
                  <MenuItem onClick={() => onDeleteClick(id)}>{T.Delete}</MenuItem>
                ) : null}
              </MenuList>
            </Menu>
          )}

          {showResolve && (
            <Tooltip placement="top-end" label={isClosed ? T.ReOpen : T.MarkAsResolved}>
              <IconButton
                data-testid="resolve"
                aria-label="Comment status"
                variant="minimal"
                size="xs"
                icon={isClosed ? <CircleCheckFilled /> : <CircleShape />}
                onFocus={(e) => e.preventDefault()}
                opacity={{ base: 1, md: isClosed ? 1 : 0 }}
                _groupHover={{ opacity: 1 }}
                _focus={{ opacity: 1 }}
                onClick={() => toggleCommentStatus?.(id, status)}
                ml={'-8px'}
              />
            </Tooltip>
          )}
          {isCommentsTrackingEnabled && isUnread && (
            <PaginationDotFilled color="fill-brand" ml={'-5px'} />
          )}
        </HStack>
        {!isBeingEdited && (
          <Box>
            {formattedTimeCode && (
              <Badge variant="timestamp" size="sm" mr="8px">
                {formattedTimeCode}
              </Badge>
            )}
            {isRichComment ? (
              <RichCommentRender
                text={text}
                editedTime={editedTime}
                isResolved={isClosed}
                searchQuery={searchQuery}
              />
            ) : (
              <>
                <Text display="inline" variant="body-md">
                  {text}
                </Text>
                {editedTime && (
                  <Tooltip placement="top" label={convertDateTime(editedTime)}>
                    <Text
                      display="inline"
                      color="text-secondary"
                      variant="body-md"
                    >{` (${T.Edited})`}</Text>
                  </Tooltip>
                )}
              </>
            )}
            {Array.isArray(textDecorations) && (
              <CommentClipPreviews
                setPreviewClip={setPreviewClip}
                textDecorations={textDecorations}
              />
            )}
          </Box>
        )}
        {showReplyButton && !isPreviousVersion && (
          <HStack spacing={50}>
            <BokehButton variant={'secondary'} size="xs" onClick={() => onReplyClick?.(id)}>
              {T.Reply}
            </BokehButton>
          </HStack>
        )}
      </VStack>
    </HStack>
  );
};
